import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, BookOpen, Clock, Award, ShieldCheck, CheckCircle2, 
  Send, ChevronDown, Check, Loader2, Sparkles, User, Monitor
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const courseDetails = {
  "sap-abap": {
    title: "SAP-ABAP",
    subtitle: "Master the Foundational Programming Language of SAP Systems",
    desc: "SAP ABAP (Advanced Business Application Programming) is the backbone of customizing and developing custom objects in SAP ERP and S/4HANA systems. This comprehensive, industry-aligned training program takes you from programming fundamentals to advanced enterprise application development, preparing you for real-world projects and professional SAP certifications.",
    duration: "10 Weeks",
    level: "Beginner to Intermediate",
    mode: "Live Interactive Classes & Virtual Labs",
    certName: "C_TAW12_750 (SAP Certified Development Associate - ABAP)",
    topics: ["ABAP Dictionary", "Reports & ALV", "Modularization", "Forms & Layouts", "BAPIs & Enhancements"],
    audience: [
      "Software developers seeking to transition into the lucrative SAP ecosystem",
      "Fresh graduates with a degree in Computer Science, IT, or related fields",
      "SAP Functional consultants looking to understand technical layers",
      "IT professionals wanting to get certified in SAP development"
    ],
    highlights: [
      "100+ Hours of Practical Hands-on Labs & Exercises",
      "24/7 Access to Live SAP Sandbox Environments",
      "Mentorship from Certified Lead SAP Developers",
      "Real-world Capstone Project (Custom Invoice & Report Setup)",
      "Complete preparation for the SAP ABAP Associate Certification Exam"
    ],
    syllabus: [
      {
        title: "Module 1: SAP Architecture & ABAP Fundamentals",
        items: [
          "Understanding ERP, SAP R/3, ECC, and S/4HANA systems",
          "ABAP Development Environment (SAP GUI, Eclipse ADT)",
          "Variables, Data Types, Control Structures, and Operators",
          "Designing user-friendly Selection Screens"
        ]
      },
      {
        title: "Module 2: ABAP Dictionary (Data Modeling & Storage)",
        items: [
          "Creating Custom Database Tables, Fields, Domains, and Data Elements",
          "Implementing Search Helps, Table Views, and Structures",
          "Configuring Table Maintenance Generators (TMG)",
          "Defining Foreign Key relationships & database locking"
        ]
      },
      {
        title: "Module 3: Open SQL & Internal Tables",
        items: [
          "Writing optimized queries using Open SQL (SELECT, INSERT, UPDATE, DELETE)",
          "Working with Internal Tables (Standard, Sorted, and Hashed)",
          "Using Work Areas, Field Symbols, and Control Level Processing",
          "Query Optimization & SQL performance tuning guidelines"
        ]
      },
      {
        title: "Module 4: Classical, Interactive & ALV Reporting",
        items: [
          "Creating classical and interactive report lists",
          "Building grid displays using ABAP List Viewer (ALV) Function Modules",
          "Object-Oriented ALV grid implementation in container controls",
          "Handling interactive user commands, custom buttons, and event triggers"
        ]
      },
      {
        title: "Module 5: Modularization & SAP Forms",
        items: [
          "Writing reusable code via Subroutines and Function Modules (RFCs)",
          "Introduction to Object-Oriented ABAP (Classes, Methods, Interfaces)",
          "Designing professional customer-facing layouts with Smart Forms",
          "Creating print layouts using Adobe Document Services (Adobe Forms)"
        ]
      },
      {
        title: "Module 6: Enhancements & Integrations",
        items: [
          "Extending standard SAP programs (User Exits, Customer Exits, BAdIs)",
          "Working with the Enhancement Framework and Source Code Plug-ins",
          "Using Business Application Programming Interfaces (BAPIs)",
          "Basics of RFCs, IDocs, and ALE for cross-system data transfer"
        ]
      }
    ]
  },
  "sap-abap-on-hana": {
    title: "SAP-ABAP ON HANA",
    subtitle: "Optimize Custom Development for the High-Speed S/4HANA In-Memory Database",
    desc: "Accelerate standard code and unlock the full potential of SAP HANA. This course teaches experienced developers the Code-to-Data (Pushdown) paradigm. You will master CDS Views, AMDP (ABAP Managed Database Procedures), and Advanced Eclipse ADT tools to build high-performance backend modules on S/4HANA.",
    duration: "8 Weeks",
    level: "Advanced",
    mode: "Live Interactive Classes & Virtual Labs",
    certName: "E_HANADB_18 (SAP Certified Development Specialist - ABAP for SAP HANA)",
    topics: ["HANA Architecture", "CDS Views", "AMDP", "Code Pushdown", "Eclipse ADT"],
    audience: [
      "Experienced SAP ABAP developers needing S/4HANA upskilling",
      "SAP Technical Architects designing high-performance systems",
      "Integration developers working on S/4HANA migration projects"
    ],
    highlights: [
      "Access to SAP S/4HANA and Eclipse ADT environments",
      "Focus on Code Pushdown and Database Optimization techniques",
      "Learn SQLScript to write efficient database procedures",
      "Course designed by senior consultants with S/4HANA migration experience"
    ],
    syllabus: [
      {
        title: "Module 1: SAP HANA Architecture & Eclipse ADT",
        items: [
          "Introduction to SAP HANA In-Memory Computing & column stores",
          "Setting up and navigating ABAP Development Tools (ADT) in Eclipse",
          "Comparing Traditional ABAP with ABAP on HANA paradigms",
          "Working with ADT features: Quick Outline, SQL Console, and Debugger"
        ]
      },
      {
        title: "Module 2: Code Profiling & Static Code Analysis",
        items: [
          "Using the ABAP Test Cockpit (ATC) for HANA readiness checks",
          "Database performance tracing using ST05, SAT, and SQL Monitor (SQLM)",
          "Identifying performance bottlenecks in legacy custom ABAP code",
          "Rules for writing database-friendly Open SQL in S/4HANA"
        ]
      },
      {
        title: "Module 3: Core Data Services (CDS) Views",
        items: [
          "Defining Data Definitions, CDS View syntax, and DDL/DCL structures",
          "Working with Joins, Unions, Associations, and Input Parameters in CDS",
          "Using CDS Annotations for analytical and UI/Fiori integration",
          "Creating CDS Table Functions and Auth Configurations"
        ]
      },
      {
        title: "Module 4: ABAP Managed Database Procedures (AMDP)",
        items: [
          "Introduction to database-level procedures and SQLScript language",
          "Creating AMDP Classes, Interfaces, and Methods",
          "Debugging SQLScript and AMDP executions in Eclipse ADT",
          "Comparing when to use CDS Views versus AMDP"
        ]
      },
      {
        title: "Module 5: Fiori/OData Exposure & IDA ALV",
        items: [
          "Using ALV with Integrated Data Access (IDA) on HANA",
          "Exposing CDS views directly as OData services (SADL framework)",
          "Integrating HANA CDS views with SAP Gateway Service Builder (SEGW)",
          "Best practices for building S/4HANA enterprise-ready backend services"
        ]
      }
    ]
  },
  "sap-ui5-fiori-neptune": {
    title: "SAP-UI5/FIORI/NEPTUNE",
    subtitle: "Build Beautiful, Responsive User Experiences for SAP Enterprise Portals",
    desc: "Transform legacy, rigid SAP GUI screens into modern, responsive, and mobile-friendly web applications. This training covers the entire frontend development lifecycle in SAP. Learn the fundamentals of HTML5/JS, dive deep into SAPUI5 architecture, explore SAP Fiori design guidelines, and learn to build apps rapidly with the Neptune DX Platform.",
    duration: "10 Weeks",
    level: "Intermediate to Advanced",
    mode: "Live Interactive & Hands-on UX Labs",
    certName: "C_FIORIDEV_22 (SAP Certified Development Associate - SAP Fiori Application Developer)",
    topics: ["SAPUI5 framework", "Fiori Design Guidelines", "OData Integration", "Neptune DX Platform", "VS Code & Web IDE"],
    audience: [
      "SAP developers wishing to transition into modern frontend/web developer roles",
      "Web designers and HTML5 developers seeking entry into the SAP UX market",
      "Product managers and enterprise architects aiming to modernize system layouts"
    ],
    highlights: [
      "Build and deploy 5 fully-functional Fiori and Neptune apps",
      "Hands-on configuration of the SAP Fiori Launchpad",
      "Integrate backend SAP systems via Gateway and OData APIs",
      "Master the Neptune Application Designer for rapid application development (low-code UX)"
    ],
    syllabus: [
      {
        title: "Module 1: Web Development & SAPUI5 Core",
        items: [
          "Fundamentals of JavaScript (ES6+), CSS, and HTML5",
          "Introduction to the SAPUI5 framework and its MVC architecture",
          "UI5 Controls, Layouts, and XML views",
          "Resource handling, internationalization (i18n), and localization"
        ]
      },
      {
        title: "Module 2: Data Binding & Models",
        items: [
          "Understanding JSON, XML, and Resource models in SAPUI5",
          "Implementing OData models for backend data communication",
          "One-way, two-way, and one-time data binding types",
          "Formatters, sorting, filtering, and grouping list data"
        ]
      },
      {
        title: "Module 3: SAP Fiori Design Guidelines & Launchpad",
        items: [
          "Core Fiori UX principles: Role-based, responsive, coherent, simple, delightful",
          "Standard SAP Fiori floorplans (Master-Detail, Worklist, Wizard)",
          "Configuring catalogs, groups, roles, and tiles on Fiori Launchpad",
          "Extending standard Fiori Apps using UI5 Adaptation Projects"
        ]
      },
      {
        title: "Module 4: OData Services Consumption",
        items: [
          "Consuming standard Gateway OData services",
          "Handling CRUD operations in SAPUI5 apps",
          "Batch requests ($batch) for optimized network performance",
          "Error handling and loading indicators in UI5 components"
        ]
      },
      {
        title: "Module 5: Neptune DX Platform (DXP) Development",
        items: [
          "Introduction to Neptune DX Platform (Planet 8 and Planet 9)",
          "Building rapid mobile & desktop apps using Neptune Application Designer",
          "Neptune Application Management (NAM) and Mobile Client builds",
          "Connecting Neptune apps to SAP Class systems via API helper classes"
        ]
      }
    ]
  },
  "sap-mm": {
    title: "SAP-MM",
    subtitle: "Streamline Procurement and Master Inventory Management Operations",
    desc: "SAP Materials Management (MM) is a core module in SAP ERP that manages procurement activities and inventory tracking. This training program covers standard business workflows, from raising purchase requisitions and managing master records (Material & Vendor) to executing inventory checks, physical counts, and logistic invoice verification.",
    duration: "8 Weeks",
    level: "Beginner to Intermediate",
    mode: "Live Interactive & Case Studies",
    certName: "C_TS452 (SAP Certified Application Associate - SAP S/4HANA Sourcing and Procurement)",
    topics: ["Procurement Cycle", "Inventory Management", "Material Valuation", "Invoice Verification", "Stock Movements"],
    audience: [
      "Procurement, logistics, or warehouse managers wishing to upgrade to SAP",
      "Business analysts and supply chain graduates starting an ERP career",
      "Functional consultants aiming to expand their knowledge of logistics modules"
    ],
    highlights: [
      "Work on simulated retail and manufacturing supply chain scenarios",
      "Deep-dive into S/4HANA Sourcing and Procurement configurations",
      "Hands-on setups of custom pricing procedures and release strategies",
      "Resume review and placement support tailored for SAP supply chain roles"
    ],
    syllabus: [
      {
        title: "Module 1: Navigation & Material Master Data",
        items: [
          "Overview of S/4HANA Logistics enterprise structure",
          "Material Master: Industry sectors, material types, and view configs",
          "Business Partner (Vendor Master) creation and role definitions",
          "Purchasing Info Records (PIR) and Source Lists"
        ]
      },
      {
        title: "Module 2: The Procurement Cycle (P2P)",
        items: [
          "Generating Purchase Requisitions (PR) and Request for Quotes (RFQ)",
          "Creating and releasing Purchase Orders (PO)",
          "Managing Goods Receipt (GR) and posting stock movements",
          "Executing Logistics Invoice Verification (LIV) and block handling"
        ]
      },
      {
        title: "Module 3: Special Procurement Procedures",
        items: [
          "Subcontracting and Consignment workflows",
          "Stock Transfer Orders (STO) between plants and storage locations",
          "Pipeline materials and Third-party procurement processing",
          "Configuring Outline Agreements: Contracts and Scheduling Agreements"
        ]
      },
      {
        title: "Module 4: Pricing & Release Configuration",
        items: [
          "Defining Condition Tables, Access Sequences, and Condition Types",
          "Setting up custom Pricing Schema/Calculation Schemas",
          "Configuring Release Procedures with Classification for PR and PO approval",
          "Automatic Account Determination (Integration with SAP FICO)"
        ]
      },
      {
        title: "Module 5: Inventory Management & Physical Inventory",
        items: [
          "Goods Movements: Movement Types, Goods Issue, and Transfer Postings",
          "Managing blocked stock, quality inspection stock, and unrestricted stock",
          "Physical Inventory: Creating docs, entering counts, and posting differences",
          "Reporting tools: MB51, MMBE, and SAP S/4HANA Fiori analytics"
        ]
      }
    ]
  },
  "sap-fico": {
    title: "SAP-FICO",
    subtitle: "Establish Financial Control and Global Accounting Compliance",
    desc: "SAP Financial Accounting (FI) and Controlling (CO) is the core module for reporting financial status and managing internal cost control. In this intensive course, you will learn to configure and operate the General Ledger, Accounts Payable, Accounts Receivable, Asset Accounting, Cost Center Accounting, and Profitability Analysis (CO-PA) workflows in S/4HANA Finance.",
    duration: "10 Weeks",
    level: "Beginner to Advanced",
    mode: "Live Interactive & Case Studies",
    certName: "C_TS4FI (SAP Certified Application Associate - SAP S/4HANA Financial Accounting)",
    topics: ["General Ledger (G/L)", "AP & AR Processes", "Asset Accounting", "Cost Center Accounting", "CO-PA / Profitability"],
    audience: [
      "Accountants, finance managers, and CPA/CA professionals transitioning to ERP",
      "Business analysts and MBA Finance graduates starting their careers",
      "IT consultants wanting to specialize in the highly demanding SAP FICO module"
    ],
    highlights: [
      "Step-by-step configuration of a global enterprise company code",
      "Covers classic ECC FICO and New Asset Accounting in S/4HANA Finance",
      "Real-world financial scenarios: Month-end close, depreciation runs",
      "Hands-on integration with Sales (SD) and Procurement (MM)"
    ],
    syllabus: [
      {
        title: "Module 1: Enterprise Structure & G/L Accounting",
        items: [
          "Configuring Company Codes, Chart of Accounts, and Fiscal Year Variants",
          "Setting up Posting Periods, Document Types, and Number Ranges",
          "Creating and maintaining G/L Master Records (GL Accounts)",
          "Posting Journal Entries, Parked Documents, and foreign currency revaluation"
        ]
      },
      {
        title: "Module 2: Accounts Payable (AP) & Accounts Receivable (AR)",
        items: [
          "Configuring Vendor and Customer Account Groups and Master Data",
          "Processing vendor invoices, payments, and Automatic Payment Program (F110)",
          "Customer billing, incoming receipts, and Dunning configurations",
          "Special G/L transactions (Down payments, Bills of Exchange)"
        ]
      },
      {
        title: "Module 3: Asset Accounting (FI-AA)",
        items: [
          "Chart of Depreciation, Asset Classes, and Depreciation Areas configuration",
          "Asset acquisition, transfer, retirement, and sale workflows",
          "Configuring Depreciation Keys and running depreciation programs",
          "New Asset Accounting architecture in SAP S/4HANA Finance"
        ]
      },
      {
        title: "Module 4: Controlling Fundamentals (CO) & Cost Centers",
        items: [
          "Defining Controlling Areas, Number Ranges, and Cost Elements",
          "Configuring Cost Center Accounting (CCA) and Activity Types",
          "Executing Internal Orders and budgeting controls",
          "Statistical Key Figures and distributions/assessments allocation methods"
        ]
      },
      {
        title: "Module 5: Product Costing & Profitability Analysis (CO-PA)",
        items: [
          "Product Cost Planning: Cost Estimate with Quantity Structure",
          "BOM, Routing integration, and Work Center costing settings",
          "Profit Center Accounting (PCA) structure and data flow",
          "Introduction to Margin Analysis / CO-PA structures in S/4HANA"
        ]
      }
    ]
  },
  "sap-sd": {
    title: "SAP-SD",
    subtitle: "Optimize the Order-to-Cash Cycle and Drive Sales Performance",
    desc: "SAP Sales and Distribution (SD) handles customer relationships, sales orders, shipping, billing, and credit management. This course trains you in configuring and operating the entire Order-to-Cash (O2C) cycle. You will learn to manage pricing procedures, shipping points, billing document controls, and integration with FICO and MM.",
    duration: "8 Weeks",
    level: "Beginner to Intermediate",
    mode: "Live Interactive & Case Studies",
    certName: "C_TS462 (SAP Certified Application Associate - SAP S/4HANA Sales)",
    topics: ["Sales Cycles (O2C)", "Pricing & Conditions", "Shipping & Routing", "Billing & Invoicing", "Credit Management"],
    audience: [
      "Sales, marketing, or retail executives transitioning to SAP operations",
      "Business analysts and supply chain consultants seeking specialization",
      "Graduates wanting to launch a career in ERP sales backend configuration"
    ],
    highlights: [
      "End-to-end setup of the Order-to-Cash cycle in a sandbox system",
      "Master S/4HANA Sales configurations (Business Partners, Pricing)",
      "Covers complex pricing techniques (Condition scales, formulas)",
      "Preparation for the SAP Sales Application Associate Exam"
    ],
    syllabus: [
      {
        title: "Module 1: Enterprise Structure & Master Data",
        items: [
          "Defining Sales Organizations, Distribution Channels, and Divisions",
          "Creating Sales Offices, Sales Groups, and Shipping Points",
          "Business Partner (Customer Master) and Material Master setups",
          "Customer-Material Info Records and Customer Hierarchies"
        ]
      },
      {
        title: "Module 2: Sales Document Processing",
        items: [
          "Standard Sales Cycles: Inquiry, Quotation, and Sales Orders",
          "Special sales workflows: Rush Orders, Cash Sales, Consignments",
          "Processing complaints: Credit Memo requests, Debit Memo requests, Returns",
          "Item Categories and Schedule Line Categories determination controls"
        ]
      },
      {
        title: "Module 3: Pricing & Condition Techniques",
        items: [
          "Defining Condition Tables, Access Sequences, and Condition Types",
          "Setting up custom Pricing Procedures and pricing determination rules",
          "Configuring Condition Scales, Discounts, Freights, and Taxes",
          "Implementing Material Determination, Listing, and Exclusion"
        ]
      },
      {
        title: "Module 4: Shipping, Transportation & Delivery",
        items: [
          "Delivery document creation, item categories, and delivery types",
          "Configuring picking, packing, and Goods Issue (GI) processing",
          "Shipping Point Determination and Route Determination mechanisms",
          "Integration with Warehousing (WM/EWM) for delivery processes"
        ]
      },
      {
        title: "Module 5: Billing & Integration",
        items: [
          "Billing Document Types (Invoices, Credit/Debit Memos, Proforma)",
          "Configuring Billing Plan (Milestone vs. Periodic) and billing blocks",
          "Copy control rules between sales, delivery, and billing documents",
          "Revenue Account Determination (Integration with SAP FICO)"
        ]
      }
    ]
  },
  "sap-hr-hcm": {
    title: "SAP-HR/HCM",
    subtitle: "Manage Enterprise Workforce, Payroll, and Talent Development",
    desc: "SAP Human Capital Management (HCM) is the premier system for workforce management. This course covers everything from organizational structure setup and personnel administration to time management sheets, payroll processing, and employee/manager self-services (ESS/MSS). You will master configurations to manage employee records, benefits, and local payroll compliance rules.",
    duration: "8 Weeks",
    level: "Beginner to Intermediate",
    mode: "Live Interactive & Case Studies",
    certName: "C_THR12 (SAP Certified Application Associate - Human Capital Management)",
    topics: ["Personnel Admin", "Org Management", "Time Management", "Payroll Processing", "ESS & MSS Portals"],
    audience: [
      "HR managers, recruiters, and personnel administrators upskilling to ERP",
      "Business analysts and HR graduates starting an SAP consultancy role",
      "Technical consultants wanting to learn HCM backend structures and Infotypes"
    ],
    highlights: [
      "Build organizational charts and manage employee hierarchies from scratch",
      "Configure time recording schedules, holiday calendars, and quotas",
      "Understand payroll calculation schemas and wage type mappings",
      "Direct guidance from senior certified SAP HCM consultants"
    ],
    syllabus: [
      {
        title: "Module 1: Organizational Management (OM)",
        items: [
          "Organizational structures: Organizational Units, Jobs, Positions, and Tasks",
          "Maintaining OM Objects using Simple Maintenance and Detail Maintenance",
          "Understanding matrix structures and object relationships (A/B relations)",
          "Integrating Org Management with Personnel Administration"
        ]
      },
      {
        title: "Module 2: Personnel Administration (PA)",
        items: [
          "Enterprise Structure vs. Personnel Structure (Company Codes, Personnel Areas)",
          "Working with employee master data Infotypes (Actions, Organizational Assignment)",
          "Configuring Infotype menus, screen modifications, and features (PINCH, NUMKR)",
          "Setting up Personnel Actions (Hiring, Promotion, Termination, Retirement)"
        ]
      },
      {
        title: "Module 3: Time Management",
        items: [
          "Configuring Public Holiday Calendars and Work Schedules (Daily, Period)",
          "Absences, Attendances, and Attendance/Absence Quotas configuration",
          "Time recording options: Cross-Application Time Sheet (CATS) and IT2001-2005",
          "Evaluating time sheets: Work schedules vs. actual attendance"
        ]
      },
      {
        title: "Module 4: Payroll Configuration & Wage Types",
        items: [
          "Payroll Org Structure: Payroll Area, Control Record, and Payroll Period",
          "Creating Wage Types and configuring Wage Type characteristics (T511, T512W)",
          "Understanding Payroll schemas, rules (PCRs), and processing blocks",
          "Month-end payroll runs, pay slip generation, and posting to G/L"
        ]
      },
      {
        title: "Module 5: Employee Self-Service (ESS) & Manager Self-Service (MSS)",
        items: [
          "Overview of ESS and MSS portal structures and configurations",
          "Employee workflows: Leave requests, profile updates, and payslip downloads",
          "Manager workflows: Leave approvals, appraisal reviews, and team tracking",
          "Integrating SAP HCM on-premise with cloud-based SuccessFactors"
        ]
      }
    ]
  },
  "sap-ariba": {
    title: "SAP-ARIBA",
    subtitle: "Lead Modern Strategic Sourcing and Cloud Procurement Networks",
    desc: "SAP Ariba is the leading cloud-based procurement platform. This course provides comprehensive training in Ariba Strategic Sourcing, Supplier Lifecycle & Performance (SLP), Contract Management, and Buying & Invoicing (P2P). You will learn procurement digitalization, supplier onboarding, and how to integrate Ariba with S/4HANA systems.",
    duration: "6 Weeks",
    level: "Intermediate",
    mode: "Live Interactive & Cloud Labs",
    certName: "C_ARCBP (SAP Certified Application Associate - SAP Ariba Sourcing / Procurement)",
    topics: ["Ariba Network", "Strategic Sourcing", "P2P (Procure-to-Pay)", "Contract Management", "Supplier Lifecycle (SLP)"],
    audience: [
      "Procurement managers and purchasing specialists looking to master cloud ERP",
      "SAP MM consultants wishing to cross-train into cloud procurement",
      "Supply chain professionals wanting high-paying strategic sourcing roles"
    ],
    highlights: [
      "Access to simulated SAP Ariba buyer and seller cloud interfaces",
      "Step-by-step guidance in setting up requests, auctions, and contracts",
      "Learn integration architectures (CIG - Cloud Integration Gateway)",
      "Comprehensive exam preparation for Ariba Sourcing and Procurement credentials"
    ],
    syllabus: [
      {
        title: "Module 1: SAP Ariba Overview & Network Essentials",
        items: [
          "Introduction to SAP Ariba cloud procurement and Ariba Network architecture",
          "Buyer and Seller portal navigation, account setups, and dashboards",
          "Sourcing, Procurement, and Supply Chain collaboration models",
          "Configuring Cloud Integration Gateway (CIG) overview"
        ]
      },
      {
        title: "Module 2: Ariba Sourcing & Auctions",
        items: [
          "Creating Sourcing Projects, templates, and team roles",
          "Designing RFPs, RFIs, and Request for Quotes events",
          "Setting up Live Auctions (Reverse, Forward, Dutch, and English)",
          "Evaluating supplier bids, award scenarios, and analysis"
        ]
      },
      {
        title: "Module 3: Supplier Lifecycle & Performance (SLP)",
        items: [
          "Supplier onboarding, self-registration, and qualification templates",
          "Managing supplier requests and approval workflows",
          "Configuring Supplier Performance Management (SPM) reviews",
          "Supplier Risk analysis and mitigation workflows"
        ]
      },
      {
        title: "Module 4: Ariba Contract Management",
        items: [
          "Creating contract workspaces, hierarchies, and contract clauses",
          "Configuring contract templates, workflow approvals, and notifications",
          "Integrating Contract Management with ERP Purchase Orders",
          "E-signature setup (DocuSign integration) and contract renewals"
        ]
      },
      {
        title: "Module 5: Buying and Invoicing (P2P)",
        items: [
          "Catalog management: PunchOut and static catalogs",
          "Requisitions creation, validation rules, and approval hierarchies",
          "Purchase Order generation and dispatch over Ariba Network",
          "Receiving goods, processing electronic invoices, and 3-way matching rules"
        ]
      }
    ]
  },
  "sap-pm-pp-qm": {
    title: "SAP-PM/PP/QM",
    subtitle: "Synchronize Production Planning, Maintenance, and Quality Control",
    desc: "Optimize industrial shop floors and asset lifecycles. This integrated training covers three core modules: Production Planning (PP), Plant Maintenance (PM), and Quality Management (QM). You will learn production master data (BOMs, routings), Material Requirements Planning (MRP), plant work orders, preventive maintenance schedules, and quality inspections.",
    duration: "12 Weeks",
    level: "Intermediate to Advanced",
    mode: "Live Interactive & Manufacturing Case Studies",
    certName: "C_TS422 (SAP S/4HANA Production Planning) / C_TS413 (S/4HANA Asset Application)",
    topics: ["Production Master Data", "MRP Processing", "Preventive Maintenance", "Quality Inspections", "Shop Floor Execution"],
    audience: [
      "Manufacturing engineers, production managers, and plant supervisors",
      "Quality assurance managers and asset maintenance coordinators",
      "SAP consultants wanting to specialize in manufacturing core integrations"
    ],
    highlights: [
      "Simulate a complete manufacturing lifecycle in S/4HANA",
      "Configure complex MRP (Material Requirements Planning) scenarios",
      "Hands-on setups of preventative maintenance cycles and inspection lots",
      "Led by industrial experts with 15+ years of SAP logistics experience"
    ],
    syllabus: [
      {
        title: "Module 1: Production Planning (PP) Master Data & MRP",
        items: [
          "Work Centers, Bill of Materials (BOM), and Routings creation",
          "Demand Management: Planned Independent Requirements (PIR)",
          "Material Requirements Planning (MRP Live) execution and evaluation",
          "Planned Orders, Production Orders, and confirmation processing"
        ]
      },
      {
        title: "Module 2: Shop Floor Control & Capacity Planning",
        items: [
          "Releasing Production Orders, printing papers, and picking components",
          "Goods Issue for production and Goods Receipt from production",
          "Capacity Evaluation and Capacity Leveling (CM21)",
          "Discrete, Process, and Repetitive manufacturing types overview"
        ]
      },
      {
        title: "Module 3: Plant Maintenance (PM) Technical Objects",
        items: [
          "Technical Structure: Functional Locations, Equipment, and Bills of Material",
          "Creating Work Centers, Task Lists, and Measuring Points",
          "Corrective Maintenance: Maintenance Notifications and Maintenance Orders",
          "Order execution, time confirmation, and Technical Completion (TECO)"
        ]
      },
      {
        title: "Module 4: Preventive Maintenance & Service Plans",
        items: [
          "Configuring Maintenance Strategies and Maintenance Cycles",
          "Creating single-cycle and strategy-based Maintenance Plans",
          "Scheduling plans, generating maintenance orders automatically",
          "Maintenance cost calculations and settlements to cost centers"
        ]
      },
      {
        title: "Module 5: Quality Management (QM) Inspections",
        items: [
          "QM Master Data: Inspection Characteristics (MIC), Methods, and Catalog Types",
          "Quality Inspection Plans and inspection lot generation (Grades/Samples)",
          "Results Recording, Usage Decision (UD), and posting stock from quality",
          "Quality Notifications: Customer complaints and vendor complaints"
        ]
      }
    ]
  },
  "sap-bi-bw": {
    title: "SAP-BI/BW",
    subtitle: "Architect Robust Data Warehouses and Deliver Enterprise Intelligence",
    desc: "Master data warehousing and analytics with SAP Business Warehouse (BW) and Business Intelligence (BI). This course covers BW data modeling (InfoObjects, Advanced DataStore Objects, InfoCubes), ETL (Extract, Transform, Load) pipelines, BEx Query Designer, and integrations with reporting systems like SAP Analytics Cloud (SAC).",
    duration: "8 Weeks",
    level: "Intermediate",
    mode: "Live Interactive & Virtual Data Labs",
    certName: "C_TBW50H (SAP Certified Application Associate - Modeling and Data Acquisition with SAP BW)",
    topics: ["Data Modeling", "ETL Pipelines", "InfoObjects & ADSOs", "BEx Query Designer", "SAC Analytics Integration"],
    audience: [
      "Data analysts, database administrators, and BI engineers upskilling to SAP",
      "SAP functional consultants looking to learn data warehousing",
      "Graduates wanting to launch a career in enterprise data architecture"
    ],
    highlights: [
      "Build custom data models using modern SAP BW/4HANA structures",
      "Set up data extraction from SAP ECC, S/4HANA, and external CSV files",
      "Hands-on query design with complex variables, filters, and structures",
      "Learn to connect BW queries to SAP Analytics Cloud (SAC)"
    ],
    syllabus: [
      {
        title: "Module 1: BW/4HANA Architecture & Basics",
        items: [
          "Introduction to Data Warehousing and BW/4HANA architecture",
          "Understanding LSA++ (Layered Scalable Architecture) patterns",
          "Navigating the BW Modeling Tools in Eclipse",
          "Creating InfoAreas, InfoPackages, and BW Projects"
        ]
      },
      {
        title: "Module 2: BW Data Modeling & Objects",
        items: [
          "Creating InfoObjects: Characteristics and Key Figures",
          "Advanced DataStore Objects (ADSO) configuration (Standard, Staging)",
          "Designing CompositeProviders and InfoCubes concepts",
          "Managing master data: Text, Attributes, and Hierarchies"
        ]
      },
      {
        title: "Module 3: ETL (Extraction, Transformation, Loading)",
        items: [
          "Data Sources creation and configuration in SAP Source Systems",
          "Designing Transformations: Formulas, routines (ABAP), and mappings",
          "Configuring Data Transfer Processes (DTP) for loading data",
          "Managing Process Chains for automated scheduling and loading alerts"
        ]
      },
      {
        title: "Module 4: Business Explorer (BEx) & Query Design",
        items: [
          "Building custom reporting queries using BEx Query Designer",
          "Defining Filters, Variables, Calculated Key Figures, and Formulas",
          "Using Exceptions, Conditions, and Free Characteristics",
          "Testing query execution in RSRT analyzer"
        ]
      },
      {
        title: "Module 5: BI Analytics & SAC Integration",
        items: [
          "Exposing BW Queries as OData/Analytical queries",
          "Introduction to SAP Analytics Cloud (SAC) architecture",
          "Creating SAC Models, Stories, Charts, and Dashboards from BW data",
          "Overview of SAP BusinessObjects (BOBJ) reporting tools"
        ]
      }
    ]
  },
  "sap-wm": {
    title: "SAP-WM",
    subtitle: "Manage Advanced Inventory Locations and Automate Material Flows",
    desc: "SAP Warehouse Management (WM) provides automated controls for complex warehouses. This training course covers organizational structures (storage types, sections, bins), Transfer Requests (TR), Transfer Orders (TO), picking and put-away strategies, and RF mobile data entry integrations. You will learn to optimize warehouse space and streamline stock transfers.",
    duration: "6 Weeks",
    level: "Intermediate",
    mode: "Live Interactive & Case Studies",
    certName: "C_TS4FI (Sourcing and Procurement with Warehouse Integration)",
    topics: ["Storage Bins & Types", "Transfer Orders (TO)", "Put-away Strategies", "Picking Strategies", "RF Mobile Data Entry"],
    audience: [
      "Warehouse supervisors, logistics coordinators, and inventory managers",
      "SAP MM consultants wishing to expand into warehousing certifications",
      "Supply chain graduates seeking technical implementation skills"
    ],
    highlights: [
      "Design and map a physical warehouse layout into SAP structures",
      "Configure put-away and picking strategies for automatic bin assignments",
      "Covers integration workflows with Inventory Management (IM) and Sales (SD)",
      "Learn RF mobile terminal connectivity protocols"
    ],
    syllabus: [
      {
        title: "Module 1: Warehouse Structure & Master Data",
        items: [
          "Defining Warehouse Numbers, Storage Types, Sections, and Bins",
          "Linking Warehouse Numbers to Plants and Storage Locations",
          "Material Master views for WM: WM Unit, Palletization data",
          "Creating and maintaining Storage Bins dynamically"
        ]
      },
      {
        title: "Module 2: Warehouse Goods Movements",
        items: [
          "Understanding Transfer Requests (TR) and Posting Change Notices (PCN)",
          "Creating Transfer Orders (TO) manually and automatically",
          "Confirming Transfer Orders, handling stock differences and overages",
          "Interim Storage Areas and their role in IM-WM data flow"
        ]
      },
      {
        title: "Module 3: Put-away & Picking Strategies",
        items: [
          "Configuring Put-away strategies: Fixed bin, open storage, addition to stock",
          "Configuring Picking strategies: FIFO, LIFO, partial pallet quantity",
          "Material staging for production (Integration with PP)",
          "Delivery staging for sales orders (Integration with SD)"
        ]
      },
      {
        title: "Module 4: Physical Inventory in WM",
        items: [
          "Annual inventory, continuous inventory, and cycle counting methods",
          "Generating Warehouse Physical Inventory documents",
          "Entering counts, differences handling between bin records and stock books",
          "Clearing differences in WM and posting to Inventory Management (IM)"
        ]
      },
      {
        title: "Module 5: Mobile Data Entry & RF Integration",
        items: [
          "Setting up Barcode Scanning / Radio Frequency (RF) settings",
          "Executing goods receipt, put-away, and picking via RF transactions",
          "Understanding standard RF queues and queue assignments",
          "Introduction to Decentralized WM and SAP EWM (Extended Warehouse Management)"
        ]
      }
    ]
  },
  "sap-success-factor": {
    title: "SAP-SUCCESS FACTOR",
    subtitle: "Lead Cloud HR Modernization and Global Talent Management",
    desc: "SAP SuccessFactors is the leading cloud-based Human Capital Management (HCM) platform. This course provides comprehensive training in Employee Central (Core HR), Recruiting, Onboarding, Performance & Goals, and LMS. You will learn to manage global employee databases, configure HR workflows, and maintain enterprise role permissions.",
    duration: "8 Weeks",
    level: "Intermediate",
    mode: "Live Interactive & Cloud Labs",
    certName: "C_THR81 (SAP Certified Application Associate - SAP SuccessFactors Employee Central)",
    topics: ["Employee Central", "Recruiting & Onboarding", "Performance & Goals", "LMS (Learning Management)", "Role-Based Permissions"],
    audience: [
      "HR professionals wanting to specialize in cloud HR software consulting",
      "On-premise SAP HR consultants transitioning to cloud SuccessFactors",
      "Systems administrators managing enterprise SaaS configurations"
    ],
    highlights: [
      "Access to SAP SuccessFactors Provisioning & Instance environments",
      "Configure metadata framework (MDF) objects and business rules",
      "Build workflows for hire, promotions, and organizational changes",
      "Direct preparation for the Employee Central Certification Exam"
    ],
    syllabus: [
      {
        title: "Module 1: SuccessFactors Platform & Security",
        items: [
          "Overview of SuccessFactors Cloud Suite and Provisioning settings",
          "Managing home page configurations, themes, and branding",
          "Role-Based Permissions (RBP) setup: Groups, Roles, and Rules",
          "Importing/exporting employee data and basic data structures"
        ]
      },
      {
        title: "Module 2: Employee Central (Core HR)",
        items: [
          "Configuring Foundation Objects (Corporate Data Model): Legal Entity, Location",
          "Managing Employee Files (HR Data Model): Personal, Job Information",
          "Understanding MDF (Metadata Framework) and creating Custom Objects",
          "Writing Business Rules for automation and field validations"
        ]
      },
      {
        title: "Module 3: Recruiting & Onboarding",
        items: [
          "Configuring Job Requisitions, Job Postings, and Candidate Portals",
          "Managing applicant status configuration and interview scheduling",
          "Setting up the Onboarding portal, new-hire checklists, and E-Verify",
          "Transitioning candidate files directly into Employee Central"
        ]
      },
      {
        title: "Module 4: Performance & Goals Management",
        items: [
          "Designing Goal Templates, Goal libraries, and Cascading Goals",
          "Configuring Performance Appraisal forms and route maps (Review stages)",
          "Executing 360-degree feedback reviews and calibration processes",
          "Goal integration with annual appraisal cycles"
        ]
      },
      {
        title: "Module 5: Learning Management System (LMS)",
        items: [
          "Configuring items, curricula, scheduled offerings, and content servers",
          "Managing users, assignments profiles, and learning records",
          "Setting up training evaluations and reporting dashboards",
          "Integrating SuccessFactors Learning with Employee Central"
        ]
      }
    ]
  }
};

export default function SAPCourseDetailPage() {
  const { courseSlug } = useParams();
  const course = courseDetails[courseSlug];

  const [form, setForm] = useState({ name: "", email: "", phone: "", batch: "Weekdays Batch", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [activeModule, setActiveModule] = useState(0);

  useEffect(() => {
    if (course) {
      document.title = `${course.title} Training | EYESAP Technology`;
    }
  }, [course]);

  if (!course) {
    return (
      <div className="pt-32 pb-24 text-center min-h-[60vh] flex flex-col items-center justify-center">
        <h2 className="font-heading font-bold text-3xl text-slate-800 mb-4">Course Not Found</h2>
        <p className="text-slate-600 mb-8">We couldn't find the requested SAP Training program.</p>
        <Link to="/sap-training">
          <Button className="bg-[#1ab69e] hover:bg-[#149380] text-white rounded-full">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to SAP Training
          </Button>
        </Link>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    
    const enrollmentMessage = `
--- ENROLLMENT INQUIRY ---
Course: ${course.title}
Preferred Batch: ${form.batch}

User Message:
${form.message || "No additional comments."}
    `.trim();

    try {
      await axios.post(`${API}/api/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: `Enrollment: ${course.title} (${form.batch})`,
        message: enrollmentMessage
      });
      setSuccess(true);
      setForm({ name: "", email: "", phone: "", batch: "Weekdays Batch", message: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid={`sap-course-detail-${courseSlug}`} className="bg-[#FAFBFD] min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-[#0F172A]">
        {/* Soft glowing background element */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#1ab69e]/15 rounded-full blur-[128px]" />
          <div className="absolute top-20 -left-20 w-80 h-80 bg-blue-500/10 rounded-full blur-[96px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <Link to="/sap-training" className="inline-flex items-center text-sm text-[#1ab69e] hover:text-[#149380] font-medium mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Training
          </Link>
          
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6 }}
          >
            <div className="flex flex-wrap gap-3 items-center mb-4">
              <span className="bg-[#1ab69e]/10 text-[#1ab69e] border border-[#1ab69e]/20 text-xs font-semibold uppercase tracking-wider px-3.5 py-1.5 rounded-full">
                {course.level}
              </span>
              <span className="bg-slate-800 text-slate-300 text-xs font-semibold px-3.5 py-1.5 rounded-full flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-[#1ab69e]" /> {course.duration}
              </span>
            </div>
            
            <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-tight max-w-4xl">
              {course.title} Training Program
            </h1>
            <p className="text-[#94A3B8] font-body text-lg mt-4 max-w-3xl leading-relaxed">
              {course.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content Layout */}
      <section className="py-16 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
          
          {/* Left Column: Course details & syllabus */}
          <div className="lg:col-span-2 space-y-12">
            
            {/* Overview */}
            <div className="bg-white rounded-2xl p-8 border border-slate-200/80 shadow-sm">
              <h2 className="font-heading font-bold text-2xl text-[#0F172A] mb-4 flex items-center gap-2">
                <BookOpen className="w-6 h-6 text-[#1ab69e]" /> Course Overview
              </h2>
              <p className="text-slate-600 font-body leading-relaxed text-base">
                {course.desc}
              </p>
            </div>

            {/* Course Highlights */}
            <div>
              <h3 className="font-heading font-bold text-xl text-[#0F172A] mb-5">Key Highlights & Inclusions</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {course.highlights.map((highlight, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-4 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                    <ShieldCheck className="w-5 h-5 text-[#1ab69e] shrink-0 mt-0.5" />
                    <span className="text-slate-700 font-body text-sm font-medium leading-normal">{highlight}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Target Audience */}
            <div className="bg-white rounded-2xl p-8 border border-slate-200/80 shadow-sm">
              <h3 className="font-heading font-bold text-xl text-[#0F172A] mb-4">Who Should Enroll?</h3>
              <div className="space-y-3.5">
                {course.audience.map((item, idx) => (
                  <div key={idx} className="flex gap-3 items-start">
                    <CheckCircle2 className="w-5 h-5 text-[#1ab69e] shrink-0 mt-0.5" />
                    <span className="text-slate-600 font-body text-sm leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Course Syllabus */}
            <div>
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-heading font-bold text-xl text-[#0F172A]">Detailed Curriculum</h3>
                <span className="text-xs text-slate-500 font-body font-semibold uppercase tracking-wider bg-slate-100 px-3 py-1 rounded-full">
                  {course.syllabus.length} Modules
                </span>
              </div>
              <div className="space-y-3">
                {course.syllabus.map((mod, modIdx) => (
                  <div 
                    key={modIdx} 
                    className="border border-slate-200/80 bg-white rounded-xl overflow-hidden shadow-sm"
                  >
                    <button
                      onClick={() => setActiveModule(activeModule === modIdx ? -1 : modIdx)}
                      className="flex items-center justify-between w-full p-5 text-left font-heading font-semibold text-slate-800 hover:bg-slate-50/50 transition-colors"
                    >
                      <span className="text-sm sm:text-base">{mod.title}</span>
                      <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${activeModule === modIdx ? "rotate-180" : ""}`} />
                    </button>
                    
                    <AnimatePresence initial={false}>
                      {activeModule === modIdx && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden border-t border-slate-100"
                        >
                          <div className="p-5 bg-slate-50/30 space-y-3">
                            {mod.items.map((item, itemIdx) => (
                              <div key={itemIdx} className="flex gap-2.5 items-center text-sm font-body text-slate-600">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#1ab69e]" />
                                <span>{item}</span>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* Right Column: Enrollment form & quick stats */}
          <div className="lg:col-span-1 space-y-8 lg:sticky lg:top-28">
            
            {/* Enrollment Form Card */}
            <div className="bg-white rounded-2xl border border-slate-200/80 p-6 md:p-8 shadow-md relative overflow-hidden">
              {/* Decorative top border */}
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-[#1ab69e]" />
              
              {success ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 rounded-full bg-[#f0fdfa] flex items-center justify-center mx-auto mb-5 border border-[#1ab69e]/30">
                    <Check className="w-8 h-8 text-[#1ab69e]" />
                  </div>
                  <h4 className="font-heading font-bold text-xl text-slate-900 mb-2">Registration Submitted!</h4>
                  <p className="text-slate-500 font-body text-sm leading-relaxed mb-6">
                    Our training coordinators will review your request and reach out to you within 24 business hours to complete your enrollment.
                  </p>
                  <Button 
                    onClick={() => setSuccess(false)}
                    className="bg-[#1ab69e] hover:bg-[#149380] text-white rounded-full w-full py-5 text-sm font-semibold"
                  >
                    Register Another Batch
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-5 h-5 text-[#1ab69e]" />
                    <h4 className="font-heading font-bold text-lg text-slate-900">Enroll in this Program</h4>
                  </div>
                  
                  <div className="space-y-1.5">
                    <Label htmlFor="enroll-name" className="text-xs font-semibold text-slate-700">Full Name *</Label>
                    <Input
                      id="enroll-name"
                      required
                      placeholder="Your Name"
                      value={form.name}
                      onChange={(e) => setForm({...form, name: e.target.value})}
                      className="rounded-lg border-slate-200 focus:border-[#1ab69e] focus:ring-[#1ab69e] text-sm"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="enroll-email" className="text-xs font-semibold text-slate-700">Email Address *</Label>
                    <Input
                      id="enroll-email"
                      type="email"
                      required
                      placeholder="name@email.com"
                      value={form.email}
                      onChange={(e) => setForm({...form, email: e.target.value})}
                      className="rounded-lg border-slate-200 focus:border-[#1ab69e] focus:ring-[#1ab69e] text-sm"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="enroll-phone" className="text-xs font-semibold text-slate-700">Phone Number *</Label>
                    <Input
                      id="enroll-phone"
                      type="tel"
                      required
                      placeholder="+91-XXXX-XXXXXX"
                      value={form.phone}
                      onChange={(e) => setForm({...form, phone: e.target.value})}
                      className="rounded-lg border-slate-200 focus:border-[#1ab69e] focus:ring-[#1ab69e] text-sm"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="enroll-batch" className="text-xs font-semibold text-slate-700">Preferred Batch *</Label>
                    <select
                      id="enroll-batch"
                      value={form.batch}
                      onChange={(e) => setForm({...form, batch: e.target.value})}
                      className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:border-[#1ab69e] focus:ring-1 focus:ring-[#1ab69e] font-body text-slate-700"
                    >
                      <option value="Weekdays Batch">Weekdays (Mon-Fri, 2 Hours/day)</option>
                      <option value="Weekends Batch">Weekends (Sat-Sun, 4 Hours/day)</option>
                      <option value="Self-Paced Training">Self-Paced with Lab Access</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="enroll-msg" className="text-xs font-semibold text-slate-700">Comments or Custom Requests</Label>
                    <Textarea
                      id="enroll-msg"
                      rows={3}
                      placeholder="Do you have any prior SAP experience?"
                      value={form.message}
                      onChange={(e) => setForm({...form, message: e.target.value})}
                      className="rounded-lg border-slate-200 focus:border-[#1ab69e] focus:ring-[#1ab69e] text-sm resize-none"
                    />
                  </div>

                  {error && <p className="text-red-500 font-body text-xs mt-1">{error}</p>}

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#1ab69e] hover:bg-[#149380] text-white rounded-full py-6 font-body font-semibold text-base transition-colors flex items-center justify-center gap-2 mt-4"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Submitting...
                      </>
                    ) : (
                      <>
                        Register & Secure Spot <Send className="w-4 h-4" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>

            {/* Quick Stats Panel */}
            <div className="bg-slate-900 rounded-2xl p-6 text-white border border-slate-800 shadow-sm space-y-4">
              <h5 className="font-heading font-semibold text-sm text-[#1ab69e] tracking-wide uppercase">Course Specifications</h5>
              
              <div className="space-y-3.5 divide-y divide-slate-800 text-sm">
                <div className="flex justify-between py-2 items-center">
                  <span className="text-slate-400 flex items-center gap-1.5 font-body"><Monitor className="w-4 h-4 shrink-0 text-[#1ab69e]" /> Training Mode</span>
                  <span className="font-semibold text-slate-200 font-body">{course.mode}</span>
                </div>
                <div className="flex justify-between py-2 items-center pt-3">
                  <span className="text-slate-400 flex items-center gap-1.5 font-body"><Award className="w-4 h-4 shrink-0 text-[#1ab69e]" /> Certification Prep</span>
                  <span className="font-semibold text-slate-200 font-body">Included</span>
                </div>
                <div className="flex justify-between py-2 items-center pt-3">
                  <span className="text-slate-400 flex items-center gap-1.5 font-body"><User className="w-4 h-4 shrink-0 text-[#1ab69e]" /> Level</span>
                  <span className="font-semibold text-slate-200 font-body">{course.level}</span>
                </div>
              </div>

              {course.certName && (
                <div className="bg-slate-800/40 border border-slate-700/30 rounded-xl p-3.5 mt-4">
                  <p className="text-xs text-slate-400 font-bold uppercase mb-1">Target Certification</p>
                  <p className="text-xs font-semibold text-slate-200 font-body leading-normal">{course.certName}</p>
                </div>
              )}
            </div>

            {/* Corporate Banner */}
            <div className="bg-[#1ab69e]/5 border border-[#1ab69e]/15 rounded-2xl p-6">
              <h6 className="font-heading font-bold text-slate-900 text-sm mb-1.5">Need Training for your Team?</h6>
              <p className="text-xs text-slate-600 font-body leading-relaxed mb-4">
                We offer custom-tailored SAP corporate training modules, flexible schedules, and private batches for organizations of all sizes.
              </p>
              <Link to="/contact">
                <span className="text-xs font-semibold text-[#1ab69e] hover:text-[#149380] transition-colors underline cursor-pointer">
                  Request Corporate Demo
                </span>
              </Link>
            </div>

          </div>

        </div>
      </section>
    </div>
  );
}
